import { z, type ZodType } from 'zod';
import { DeployVersionEventSchema, OptimizationStateSchema } from './sseShared.js';
import { ENGINE_FAMILIES } from './vocabulary.js';

/** NOTE: distinct from the turn stream's system_update (different payload). */
export const ConversationSystemUpdateEventSchema = z.object({
  type: z.literal('system_update'), conversation_id: z.string(),
  system_id: z.string(), version: z.number(),
  source: z.enum(['agent', 'ui', 'upload']), timestamp: z.number(),
});

/**
 * A solve param stripped because it is inert on the target engine, or (in the read
 * converter) a solve frozen to fixed. Shared by the write gate
 * (`validateSolvesForEngine`) and the read converter (`convertSolvesForEngine`).
 * Canonical source for `SolveConversionWarning`, re-exported by
 * src/services/systems/solveEngineSupport.ts.
 */
const SurfaceSolveConversionWarningSchema = z.object({
  code: z.enum(['solve_frozen', 'param_stripped']),
  /** A projected index of `arm` when `arm` is set (a beam-split arm row);
   *  otherwise an index into `surfaces[]`. */
  surface_index: z.number(),
  /** The beam-split arm the surface belongs to (1 = beam_splits[0]); absent for the primary. */
  arm: z.number().optional(),
  cell: z.enum(['radius', 'thickness', 'conic', 'glass', 'param', 'coordinate_break', 'semi_diameter']),
  /** The original solve name (e.g. 'fnumber', 'marginal_height'). */
  from: z.string(),
  /** The resulting solve name — 'fixed' for a freeze; same as `from` for a param strip. */
  to: z.string(),
  /** The engine-forbidden parameter removed (param_strip only). */
  dropped_param: z.string().optional(),
  /** Human-readable, user-facing explanation. */
  message: z.string(),
});

/** A lens-level setting omitted only from a temporary read-only engine projection.
 * Deploy readers of this variant before any producer emits it. */
const SystemSettingOmittedWarningSchema = z.object({
  code: z.literal('system_setting_omitted'),
  setting: z.literal('codev_reference_sphere'),
  message: z.string(),
});

export const SolveConversionWarningSchema = z.union([
  SurfaceSolveConversionWarningSchema,
  SystemSettingOmittedWarningSchema,
]);

/**
 * Engine-switch solve conversion notice: a stored system was run under an engine
 * that cannot honor some of its solves, so engine-only numeric solves were frozen
 * to fixed (and/or inert params stripped) before dispatch. Surfaces to the frontend
 * so the user sees that a constraint stopped being live. (Glass solves that cannot
 * be frozen reject the switch instead and never reach this event.)
 */
export const SolveConversionEventSchema = z.object({
  type: z.literal('solve_conversion'), conversation_id: z.string(),
  engine: z.enum(ENGINE_FAMILIES),
  warnings: z.array(SolveConversionWarningSchema), timestamp: z.number(),
});

export const OptimizationStartEventSchema = z.object({
  type: z.literal('optimization_start'), conversation_id: z.string(),
  state: OptimizationStateSchema, timestamp: z.number(),
});

export const OptimizationProgressEventSchema = z.object({
  type: z.literal('optimization_progress'), conversation_id: z.string(),
  state: OptimizationStateSchema, timestamp: z.number(),
});

export const OptimizationCompleteEventSchema = z.object({
  type: z.literal('optimization_complete'), conversation_id: z.string(),
  /** True only when this run wrote the top-N global-solution shelf (a global
   *  run that saved candidates). Lets the Zemax merit editor refetch the shelf
   *  exactly once for genuine new candidates. Absent otherwise (local/hammer,
   *  short-circuit global runs, and the whole Optiland track). */
  global_solutions_saved: z.boolean().optional(),
  state: OptimizationStateSchema,
  /** Null when CalculateMeritFunction returned NaN/Inf at MFE init or post-opt.
   *  merit_unknown gates merit_after only; merit_before can be null independently. */
  merit_before: z.number().nullable(),
  merit_after: z.number().nullable(),
  /** Zemax-track only: true when the worker could not read merit back post-optimize
   *  (`merit_after` is then null, not a number — the worker leaves it unset,
   *  zemax_worker/models/optimization.py merit_unknown). Absent on
   *  Optiland-track optimizers. */
  merit_unknown: z.boolean().optional(),
  /** Null when no percentage exists: a merit is unknown, or `merit_before` is 0. */
  improvement_percent: z.number().nullable(),
  converged: z.boolean(),
  elapsed_seconds: z.number(),
  /** Per-operand post-opt results for the frontend merit table — skips a
   *  re-evaluate round-trip. Omitted by Optiland-track optimizers (optimizeCommon.ts) —
   *  the Zemax router's `_format_operand_results` is the sole emitter
   *  (zemax-analysis-service/routers/optimization.py) and always sets
   *  `readback_failed` (never conditionally spread), matching the required
   *  field on the canonical src/services/merit/types.ts OperandResultSchema. */
  operand_results: z.array(z.object({
    type: z.string(), target: z.number().nullable(),
    /** Null when `readback_failed`. */
    value: z.number().nullable(),
    /** Deprecated: OpticStudio reports no such quantity — a Photonium T−V that
     *  read 0 whenever target or value was missing. Null once the orchestrator
     *  stops sending it; removed in a follow-up. */
    delta: z.number().nullable().meta({ deprecated: true }),
    /** Nullable alongside `target`: the rows that carry no target (BLNK/DMFS
     *  structural rows, non-finite Weight readbacks) carry no weight either,
     *  and 0 is a weight a real row can hold. See OptimizationOperandSchema
     *  in sseShared.ts. */
    weight: z.number().nullable(),
    /** Null when OpticStudio's Contribution cell reads back non-finite. */
    contribution_percent: z.number().nullable(),
    readback_failed: z.boolean(),
  })).optional(),
  /** Zemax-track only: solver error message when local DLS bailed mid-run.
   *  When set, the readback (state, operand_results) reflects the partial
   *  LDE state at solver-exit, not a successful optimization. */
  solver_error: z.string().optional(),
  /** Zemax-track only: OpticStudio's IOpticalSystem.UpdateStatus() readout
   *  captured after the optimizer closes (zemaxOptimizeUtils.ts), conditionally
   *  spread into the emit only when non-empty (emitOptimizationComplete).
   *  Surfaces material/glass/configuration errors the optimizer may have
   *  introduced. */
  system_status: z.string().optional(),
  /** Optiland-track route warnings (missing_glass_fence, auto-bounds, no-op-merit).
   *  The frontend toasts these directly so the user sees them even when the
   *  agent paraphrases the matching interpretation-embed out of its chat reply. */
  warnings: z.array(z.object({
    field: z.string(), message: z.string(), severity: z.string(),
  })).optional(),
  timestamp: z.number(),
});

export const OptimizationErrorEventSchema = z.object({
  type: z.literal('optimization_error'), conversation_id: z.string(),
  error: z.string(), timestamp: z.number(),
});

export const MeritFunctionUpdateEventSchema = z.object({
  type: z.literal('merit_function_update'), conversation_id: z.string(),
  merit_function_id: z.string(), version: z.number(),
  /** The version row's in-place write count: an editor already at (version,
   *  revision) is looking at its own save's echo. */
  revision: z.number(),
  /** 'imported' is the merit twin of the tolerance event's: the merit function a
   *  .zmx carried in, installed by the import follow-up. */
  source: z.enum(['optimize', 'evaluate', 'wizard', 'agent', 'ui', 'imported']), timestamp: z.number(),
});

/** The Optiland engine's saved optimization targets changed (contracts/src/
 *  optilandMerit.ts) — the Optiland twin of merit_function_update. An editor
 *  whose state is already at (version, revision) ignores it — its own save's
 *  echo; any other write reloads it. */
export const OptilandMeritUpdateEventSchema = z.object({
  type: z.literal('optiland_merit_update'), conversation_id: z.string(),
  merit_id: z.string(), version: z.number(), revision: z.number(),
  source: z.enum(['agent', 'ui']), timestamp: z.number(),
});

export const ZosapiScriptUpdateEventSchema = z.object({
  type: z.literal('zosapi_script_update'), conversation_id: z.string(),
  script_id: z.string(), version: z.number(),
  /** 'restore' is emit-only (DB column allows 'agent'|'ui'): version-bumping
   *  change every tab must reload; 'ui' is self-suppressed by ScriptRunner. */
  source: z.enum(['agent', 'ui', 'restore']), timestamp: z.number(),
});

export const ToleranceFunctionUpdateEventSchema = z.object({
  type: z.literal('tolerance_function_update'), conversation_id: z.string(),
  tolerance_function_id: z.string(), version: z.number(),
  source: z.enum(['manual', 'agent', 'ui', 'imported']), timestamp: z.number(),
});

/**
 * A tolerance run finished and was persisted to its (conversation, kind) slot
 * (#2881). Emitted AFTER the write commits, so a consumer that GETs the stored
 * runs on this event always sees the new one. The frontend uses it to hydrate
 * the tolerance panel and, for agent-sourced runs, to flag the tolerancing tab.
 */
export const ToleranceRunUpdateEventSchema = z.object({
  type: z.literal('tolerance_run_update'), conversation_id: z.string(),
  /** Present when this update terminates a run announced by tolerance_run_start. */
  run_id: z.string().optional(),
  run_kind: z.enum(['compute', 'inverse', 'quick']),
  source: z.enum(['agent', 'ui']),
  system_version: z.number().nullable(),
  tolerance_function_version: z.number(), timestamp: z.number(),
});

/** A tolerance analysis started on the worker (synchronous call or background
 *  job). Ends with `tolerance_run_update` (persisted) or `tolerance_run_error`. */
export const ToleranceRunStartEventSchema = z.object({
  type: z.literal('tolerance_run_start'), conversation_id: z.string(),
  /** The id the worker keys the run's cancel flag by; a job's is its job id. */
  run_id: z.string(),
  timestamp: z.number(),
});

/** A tolerance analysis ended without a persisted result: a worker failure,
 *  a timeout, or a user stop (`user_canceled`). */
export const ToleranceRunErrorEventSchema = z.object({
  type: z.literal('tolerance_run_error'), conversation_id: z.string(),
  run_id: z.string(), error: z.string(), user_canceled: z.boolean(), timestamp: z.number(),
});

/**
 * A store-first write's background solve re-resolution ended (#7776): the stored
 * version's solves were re-resolved by the engine after the write replied.
 * `refreshed`: the engine's values are stored in `version` — for a CODE V editor
 * save that is the saved version itself, rewritten in place, so a client showing
 * it refetches. `unchanged`: the stored values were already the engine's.
 * `failed`: nothing was stored, for `reason`.
 */
export const SolvesReconciledEventSchema = z.object({
  type: z.literal('solves_reconciled'), conversation_id: z.string(), version: z.number(),
  outcome: z.enum(['refreshed', 'unchanged', 'failed']), reason: z.string().nullable(), timestamp: z.number(),
});

export const DesignBriefUpdateEventSchema = z.object({
  type: z.literal('design_brief_updated'), conversation_id: z.string(),
  updated_by: z.enum(['agent', 'user']), updated_at: z.string(), timestamp: z.number(),
});

/** The structured spec table changed. Carries no rows — a client refetches. */
export const DesignSpecUpdateEventSchema = z.object({
  type: z.literal('design_spec_updated'), conversation_id: z.string(),
  updated_by: z.enum(['agent', 'user']), updated_at: z.string(), timestamp: z.number(),
});

/**
 * Live relay of a main-agent → merit-agent delegation into the Merit Function
 * chat panel. Carries content SNAPSHOTS (full text so far), keyed by a stable
 * `messageId`, so the frontend upserts by id and a dropped/late event self-heals
 * on the next snapshot. `activity` is live-only (not persisted). Emitted by
 * delegateToMeritAgent; consumed by useConversationStream's raw listener seam.
 */
export const MeritDelegationStreamEventSchema = z.object({
  type: z.literal('merit_delegation_stream'), conversation_id: z.string(),
  phase: z.enum(['start', 'message', 'activity', 'done']),
  messageId: z.string(),
  role: z.enum(['user', 'assistant']).optional(),
  authorSource: z.enum(['main-agent', 'merit-agent']).optional(),
  content: z.string().optional(),
  activityText: z.string().optional(),
  timestamp: z.number(),
});

/**
 * The agent asked the UI to show specific analysis viewers ("show me the MTF
 * and spot diagram"). `tabs` carries UI tab ids from
 * `frontend/constants/zemaxTabs.ts`, resolved server-side from the agent's
 * analysis enum via `src/config/zemaxAnalysisRegistry.ts` (already filtered
 * for gating). The frontend re-validates ids against its own registry and
 * drops unknowns. Owners' clients react (switch to the analysis pane, open
 * one tab or desktop windows); read-only viewers ignore it — the agent works
 * for the owner, and yanking a spectator's tabs would be hostile.
 */
export const ViewerFocusEventSchema = z.object({
  type: z.literal('viewer_focus'), conversation_id: z.string(),
  tabs: z.array(z.string()).min(1),
  /** 'replace' closes analysis windows not in `tabs` (the user asked to swap
   *  what's shown); absent/'add' leaves existing windows alone. Lens-editor
   *  windows are never closed either way. */
  mode: z.enum(['add', 'replace']).optional(),
  timestamp: z.number(),
});

/**
 * Authoritative "a turn is running on this conversation" signal, broadcast to
 * every connected client (all tabs/devices) so the UI can disable Send while the
 * agent works — even when this client isn't the one streaming the turn (a
 * judge-driven turn, another tab, or a dropped stream). Emitted from the turn
 * lifecycle (executeAgentQuery register/finally), so it reflects the real turn
 * state regardless of connection: a turn that outlives a disconnect stays active
 * here until it actually ends.
 */
export const AgentActiveEventSchema = z.object({
  type: z.literal('agent_active'), conversation_id: z.string(),
  active: z.boolean(), timestamp: z.number(),
});

/** Handshake frame (conversations.ts): seeds busy state + artifact versions
 *  so a reconnecting client can't miss an update between seed and subscribe. */
export const ConversationConnectedEventSchema = z.object({
  conversation_id: z.string(),
  timestamp: z.number(),
  agentActive: z.boolean(),
  /** Whether an optimization is in flight. A client that was watching an
   *  optimization, dropped the stream, and reconnects after it finished would
   *  otherwise miss the `optimization_complete` and stay stuck "optimizing"
   *  forever — the result itself reloads via the merit function. (In-process
   *  today; cross-replica once the optimization lane moves onto the lease —
   *  see the restructuring spec.) */
  optimizationActive: z.boolean(),
  /** Whether a tolerance analysis is in flight; same reconnect rationale. */
  toleranceRunActive: z.boolean(),
  systemVersion: z.number().optional(),
  meritFunctionVersion: z.number().optional(),
  toleranceFunctionVersion: z.number().optional(),
  zosapiScriptVersion: z.number().optional(),
});

/** Conversation-stream keepalive carries NO `type` field (unlike the turn stream's). */
export const ConversationKeepaliveEventSchema = z.object({ timestamp: z.number() });

/** Wire event name → payload schema for GET /conversations/{conversation_id}/stream. */
export const CONVERSATION_STREAM_EVENTS: Record<string, ZodType> = {
  deploy_version: DeployVersionEventSchema,
  connected: ConversationConnectedEventSchema,
  keepalive: ConversationKeepaliveEventSchema,
  system_update: ConversationSystemUpdateEventSchema,
  solve_conversion: SolveConversionEventSchema,
  solves_reconciled: SolvesReconciledEventSchema,
  optimization_start: OptimizationStartEventSchema,
  optimization_progress: OptimizationProgressEventSchema,
  optimization_complete: OptimizationCompleteEventSchema,
  optimization_error: OptimizationErrorEventSchema,
  merit_function_update: MeritFunctionUpdateEventSchema,
  optiland_merit_update: OptilandMeritUpdateEventSchema,
  zosapi_script_update: ZosapiScriptUpdateEventSchema,
  tolerance_function_update: ToleranceFunctionUpdateEventSchema,
  tolerance_run_update: ToleranceRunUpdateEventSchema,
  tolerance_run_start: ToleranceRunStartEventSchema,
  tolerance_run_error: ToleranceRunErrorEventSchema,
  design_brief_updated: DesignBriefUpdateEventSchema,
  design_spec_updated: DesignSpecUpdateEventSchema,
  merit_delegation_stream: MeritDelegationStreamEventSchema,
  viewer_focus: ViewerFocusEventSchema,
  agent_active: AgentActiveEventSchema,
};

/** The bus-driven union (backend ConversationEvent derives from this).
 *  Excludes connected/keepalive/deploy_version, which are connection frames
 *  written by the route, not bus events. */
export const ConversationStreamEventSchema = z.union([
  ConversationSystemUpdateEventSchema, SolveConversionEventSchema, SolvesReconciledEventSchema,
  OptimizationStartEventSchema, OptimizationProgressEventSchema,
  OptimizationCompleteEventSchema, OptimizationErrorEventSchema,
  MeritFunctionUpdateEventSchema, OptilandMeritUpdateEventSchema, ZosapiScriptUpdateEventSchema,
  ToleranceFunctionUpdateEventSchema, ToleranceRunUpdateEventSchema,
  ToleranceRunStartEventSchema, ToleranceRunErrorEventSchema,
  DesignBriefUpdateEventSchema, DesignSpecUpdateEventSchema,
  MeritDelegationStreamEventSchema, ViewerFocusEventSchema,
  AgentActiveEventSchema,
]);

export type ConversationStreamEvent = z.infer<typeof ConversationStreamEventSchema>;
export type SolveConversionWarning = z.infer<typeof SolveConversionWarningSchema>;
export type ConversationConnectedEvent = z.infer<typeof ConversationConnectedEventSchema>;
